import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Smartphone, Lock, Eye, EyeOff } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { sendLoginMobileCodeAction } from '@ivymobi/orbit-core/business/user';
import { showNotification } from '@ivymobi/orbit-core/business/notification';
import { queryParse } from '@ivymobi/orbit-core';
import { cn } from '../lib/utils';
import SendCodeButton from './components/SendCodeButton';
import CountryCodeSelect from './components/CountryCodeSelect';
import type { PhoneCodeForm, PasswordForm } from '../users';

interface LoginPageProps {
  login: (data: PhoneCodeForm) => void;
  loginByPassword: (data: PasswordForm) => void;
}

export default function LoginPage({ login, loginByPassword }: LoginPageProps) {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const params = queryParse(window.location.search);

  const [activeTab, setActiveTab] = useState<'phone' | 'account'>('phone');

  // ---- 手机号登录状态 ----
  const [countryCode, setCountryCode] = useState('+86');
  const [phone, setPhone] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [phoneSubmitting, setPhoneSubmitting] = useState(false);

  // ---- 账号密码登录状态 ----
  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [accountSubmitting, setAccountSubmitting] = useState(false);

  // ---- Toast（暂时使用原有 showNotification，内置 Toast 先隐藏） ----
  const showToast = useCallback((message: string, type: 'success' | 'error' = 'error') => {
    dispatch(showNotification(message, type));
  }, [dispatch]);

  // ---- 发送验证码 ----
  const doSendCode = useCallback((captchaToken: string) => {
    return (dispatch(sendLoginMobileCodeAction({ mobile: phone, countryCode, captchaToken })) as any)
      .then(
        () => showToast(t('Send Code Success'), 'success'),
        (err: any) => showToast(t(err.message, err.message) || '发送失败', 'error')
      );
  }, [phone, countryCode, dispatch, t, showToast]);

  // ---- 手机登录提交 ----
  const handlePhoneLogin = useCallback(() => {
    if (!phone || !verifyCode) {
      showToast('请填写完整信息');
      return;
    }
    if (phone.length < 8) {
      showToast('请输入正确的手机号');
      return;
    }
    setPhoneSubmitting(true);
    login({
      mobile: phone,
      code: verifyCode,
      countryCode,
      ...(params?.from ? { from: params.from } : {}),
    } as any);
    // login action handles navigation, so just reset submitting after a delay
    setTimeout(() => setPhoneSubmitting(false), 3000);
  }, [phone, verifyCode, countryCode, params, login]);

  // ---- 账号密码登录提交 ----
  const handleAccountLogin = useCallback(() => {
    if (!account || !password) {
      showToast('请填写完整信息');
      return;
    }
    setAccountSubmitting(true);
    loginByPassword({ account, password });
    setTimeout(() => setAccountSubmitting(false), 3000);
  }, [account, password, loginByPassword]);

  // ---- 输入框通用样式 ----
  const inputClass = cn(
    'tw-w-full tw-h-11 tw-px-3 tw-rounded-md tw-text-sm',
    'tw-transition-colors tw-outline-none'
  );
  const inputStyle: React.CSSProperties = {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    color: 'white',
  };

  return (
    <div className="tw-w-full">
      {/* Tab 切换 */}
      <div
        className="tw-grid tw-grid-cols-2 tw-mb-6 tw-rounded-lg tw-p-1"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <button
          type="button"
          onClick={() => setActiveTab('phone')}
          className={cn(
            'tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-py-2.5 tw-rounded-md',
            'tw-text-xs tw-font-medium tw-transition-all tw-duration-200 tw-cursor-pointer tw-border-none'
          )}
          style={
            activeTab === 'phone'
              ? {
                background: 'linear-gradient(to right, #2563eb, #06b6d4)',
                color: 'white',
              }
              : {
                background: 'transparent',
                color: 'rgba(191, 219, 254, 0.7)',
              }
          }
        >
          <Smartphone className="tw-w-3.5 tw-h-3.5" />
          {t('Login with SMS Code', '手机验证码')}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('account')}
          className={cn(
            'tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-py-2.5 tw-rounded-md',
            'tw-text-xs tw-font-medium tw-transition-all tw-duration-200 tw-cursor-pointer tw-border-none'
          )}
          style={
            activeTab === 'account'
              ? {
                background: 'linear-gradient(to right, #2563eb, #06b6d4)',
                color: 'white',
              }
              : {
                background: 'transparent',
                color: 'rgba(191, 219, 254, 0.7)',
              }
          }
        >
          <Lock className="tw-w-3.5 tw-h-3.5" />
          {t('Login with Account', '邮箱/账号')}
        </button>
      </div>

      {/* ===== 手机验证码登录 ===== */}
      {activeTab === 'phone' && (
        <form
          className="tw-space-y-4"
          onSubmit={(e) => { e.preventDefault(); handlePhoneLogin(); }}
        >
          {/* 国家代码 + 手机号 */}
          <div className="tw-space-y-2">
            <label className="tw-text-sm" style={{ color: 'rgba(191, 219, 254, 0.7)' }}>
              {t('YBT-ADMIN.YBT-ADMIN-030101.YBT-ADMIN-03010107', '手机号')}
            </label>
            <div className="tw-flex tw-gap-2">
              {/* 国家代码选择 — 全量国家数据 + 国旗 */}
              <CountryCodeSelect
                value={countryCode}
                onChange={setCountryCode}
              />
              {/* 手机号输入 */}
              <input
                type="tel"
                name="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={t('YBT-ADMIN.YBT-ADMIN-030103.YBT-ADMIN-03010320', '请输入手机号')}
                className={cn(inputClass, 'tw-flex-1')}
                autoComplete="tel-national"
                style={{
                  ...inputStyle,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'rgb(34, 211, 238)';
                  e.target.style.boxShadow = '0 0 0 2px rgba(34, 211, 238, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
          </div>

          {/* 验证码 */}
          <div className="tw-space-y-2">
            <label className="tw-text-sm" style={{ color: 'rgba(191, 219, 254, 0.7)' }}>
              {t('share.form.code', '验证码')}
            </label>
            <div className="tw-flex tw-gap-3">
              <input
                type="text"
                name="sms-code"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value)}
                placeholder={t('login.verificationCodePlaceholder', '请输入验证码')}
                className={cn(inputClass, 'tw-flex-1')}
                style={inputStyle}
                autoComplete="one-time-code"
                onFocus={(e) => {
                  e.target.style.borderColor = 'rgb(34, 211, 238)';
                  e.target.style.boxShadow = '0 0 0 2px rgba(34, 211, 238, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <SendCodeButton
                disabled={!phone || phone.length < 8}
                mobile={phone}
                countryCode={countryCode}
                doSendCode={doSendCode}
              />
            </div>
          </div>

          {/* 登录按钮 */}
          <button
            type="submit"
            disabled={phoneSubmitting}
            className={cn(
              'tw-w-full tw-h-11 tw-mt-2 tw-rounded-md tw-text-sm tw-font-medium',
              'tw-border-none tw-cursor-pointer tw-transition-all tw-duration-200',
              phoneSubmitting && 'tw-opacity-70 tw-cursor-not-allowed'
            )}
            style={{
              background: 'linear-gradient(to right, #2563eb, #06b6d4)',
              color: 'white',
              boxShadow: '0 10px 15px -3px rgba(37, 99, 235, 0.3)',
            }}
            onMouseEnter={(e) => {
              if (!phoneSubmitting) {
                (e.target as HTMLElement).style.background = 'linear-gradient(to right, #3b82f6, #22d3ee)';
              }
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLElement).style.background = 'linear-gradient(to right, #2563eb, #06b6d4)';
            }}
          >
            {phoneSubmitting ? '登录中...' : t('auth.signin', '登 录')}
          </button>
        </form>
      )}

      {/* ===== 邮箱/账号密码登录 ===== */}
      {activeTab === 'account' && (
        <form
          className="tw-space-y-4"
          onSubmit={(e) => { e.preventDefault(); handleAccountLogin(); }}
        >
          {/* 账号 */}
          <div className="tw-space-y-2">
            <label className="tw-text-sm" style={{ color: 'rgba(191, 219, 254, 0.7)' }}>
              {t('Please Enter Phone Number or Email', '手机号/邮箱')}
            </label>
            <input
              type="text"
              name="account"
              value={account}
              onChange={(e) => setAccount(e.target.value)}
              placeholder={t('Please Enter Phone Number or Email', '请输入手机号或邮箱')}
              className={inputClass}
              style={inputStyle}
              autoComplete="username"
              onFocus={(e) => {
                e.target.style.borderColor = 'rgb(34, 211, 238)';
                e.target.style.boxShadow = '0 0 0 2px rgba(34, 211, 238, 0.2)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* 密码 */}
          <div className="tw-space-y-2">
            <label className="tw-text-sm" style={{ color: 'rgba(191, 219, 254, 0.7)' }}>
              {t('Please Enter Password', '密码')}
            </label>
            <div className="tw-relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('Please Enter Password', '请输入密码')}
                className={cn(inputClass, 'tw-pr-10')}
                style={inputStyle}
                autoComplete="current-password"
                onFocus={(e) => {
                  e.target.style.borderColor = 'rgb(34, 211, 238)';
                  e.target.style.boxShadow = '0 0 0 2px rgba(34, 211, 238, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                  e.target.style.boxShadow = 'none';
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAccountLogin();
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-border-none tw-bg-transparent tw-cursor-pointer tw-p-0"
                style={{ color: 'rgba(255, 255, 255, 0.5)' }}
                onMouseEnter={(e) => { (e.target as HTMLElement).style.color = 'rgba(255, 255, 255, 0.8)'; }}
                onMouseLeave={(e) => { (e.target as HTMLElement).style.color = 'rgba(255, 255, 255, 0.5)'; }}
              >
                {showPassword ? <EyeOff className="tw-w-4 tw-h-4" /> : <Eye className="tw-w-4 tw-h-4" />}
              </button>
            </div>
          </div>

          {/* 登录按钮 */}
          <button
            type="submit"
            disabled={accountSubmitting}
            className={cn(
              'tw-w-full tw-h-11 tw-mt-2 tw-rounded-md tw-text-sm tw-font-medium',
              'tw-border-none tw-cursor-pointer tw-transition-all tw-duration-200',
              accountSubmitting && 'tw-opacity-70 tw-cursor-not-allowed'
            )}
            style={{
              background: 'linear-gradient(to right, #2563eb, #06b6d4)',
              color: 'white',
              boxShadow: '0 10px 15px -3px rgba(37, 99, 235, 0.3)',
            }}
            onMouseEnter={(e) => {
              if (!accountSubmitting) {
                (e.target as HTMLElement).style.background = 'linear-gradient(to right, #3b82f6, #22d3ee)';
              }
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLElement).style.background = 'linear-gradient(to right, #2563eb, #06b6d4)';
            }}
          >
            {accountSubmitting ? '登录中...' : t('auth.signin', '登 录')}
          </button>
        </form>
      )}

      {/* 底部链接 */}
      <div className="tw-mt-6 tw-flex tw-items-center tw-justify-between">
        {/* 微信登录 */}
        {!params?.from && Capacitor.getPlatform() !== 'ios' && (
          <Link
            to="/user/wechat"
            className="tw-text-sm tw-no-underline tw-transition-colors"
            style={{ color: 'rgba(191, 219, 254, 0.6)' }}
            onMouseEnter={(e) => { (e.target as HTMLElement).style.color = 'rgb(34, 211, 238)'; }}
            onMouseLeave={(e) => { (e.target as HTMLElement).style.color = 'rgba(191, 219, 254, 0.6)'; }}
          >
            {t('login.wechat', '微信登录')}
          </Link>
        )}
        {/* 注册链接 */}
        <Link
          to="/user/signup"
          className="tw-text-sm tw-no-underline tw-transition-colors"
          style={{ color: 'rgba(191, 219, 254, 0.6)' }}
          onMouseEnter={(e) => { (e.target as HTMLElement).style.color = 'rgb(34, 211, 238)'; }}
          onMouseLeave={(e) => { (e.target as HTMLElement).style.color = 'rgba(191, 219, 254, 0.6)'; }}
        >
          {t('auth.signup', '免费注册')}
        </Link>
      </div>

      {/* 内置 Toast 暂时隐藏，使用原有 showNotification 组件 */}
    </div>
  );
}
